/**
 * HQ sidebar navigation contract — Control Centre restructure.
 *
 * FIVE primary sections only. Legacy detail routes (leads, businesses,
 * websites, optimise/*, sales/*, invoices, activity) remain MOUNTED
 * so deep-links and existing screens keep working. They are simply not
 * present on the primary sidebar — the new sections surface them via
 * tabs and deep-links.
 *
 * All `to` values are absolute `/hq/...` paths on purpose. Relative
 * links inside a splat parent route (`/hq/*` → nested `<Routes>`) resolve
 * against the current URL, which was the exact regression that mis-routed
 * clicks previously.
 */
import { LayoutDashboard, Users, CreditCard, Send, Settings } from "lucide-react";

export const NAV = [
  { to: "/hq/dashboard",     label: "Dashboard",     icon: LayoutDashboard, matchPrefix: "dashboard", roles: ["owner", "admin", "sales"] },
  { to: "/hq/clients",       label: "Clients",       icon: Users,           matchPrefix: "clients",   roles: ["owner", "admin", "sales"] },
  { to: "/hq/subscriptions", label: "Subscriptions", icon: CreditCard,      matchPrefix: "subscriptions", roles: ["owner", "admin"] },
  { to: "/hq/campaigns",     label: "Campaigns",     icon: Send,            matchPrefix: "campaigns", roles: ["owner", "admin", "sales"] },
  { to: "/hq/settings",      label: "Settings",      icon: Settings,        matchPrefix: "settings",  roles: ["owner", "admin"] },
];

/**
 * Filter nav items visible to the current admin's role. Everyone with a
 * missing/unknown role falls back to the OWNER whitelist so the sidebar
 * stays usable even if the /auth/me shape drifts.
 */
export function visibleNav(role) {
  const r = (role || "owner").toLowerCase();
  return NAV.filter((n) => !n.roles || n.roles.includes(r));
}

/**
 * Legacy routes that are still mounted in HQRoot but not on the sidebar.
 * Kept here so `pageTitle` can still render a sensible breadcrumb when
 * users deep-link into them.
 */
const LEGACY_TITLES = [
  { prefix: "leads",                  label: "Clients · Lead" },
  { prefix: "businesses",             label: "Clients" },
  { prefix: "contacts",               label: "Clients · Contact" },
  { prefix: "websites",               label: "Clients · Website" },
  { prefix: "optimise/scans",         label: "Optimise · Scans" },
  { prefix: "optimise/monitoring",    label: "Subscriptions · Monitoring" },
  { prefix: "optimise/commercial",    label: "Subscriptions · Commercial" },
  { prefix: "optimise/fix",           label: "Optimise · Orange Fix" },
  { prefix: "sales/prospects",        label: "Campaigns · Prospects" },
  { prefix: "sales/campaigns",        label: "Campaigns · Campaign" },
  { prefix: "sales/opportunities",    label: "Campaigns · Opportunity" },
  { prefix: "sales",                  label: "Campaigns" },
  { prefix: "invoices",               label: "Clients · Invoice" },
  { prefix: "activity",               label: "Settings · Activity" },
];

/**
 * Resolve the page title for a given HQ pathname.
 * Prefers the longest matching prefix so "sales/prospects" beats "sales".
 */
export function pageTitle(pathname) {
  const rest = pathname.replace(/^\/hq\/?/, "");
  const all = [
    ...NAV.map((n) => ({ prefix: n.matchPrefix, label: n.label })),
    ...LEGACY_TITLES,
  ];
  const sorted = [...all].sort(
    (a, b) => (b.prefix?.length || 0) - (a.prefix?.length || 0),
  );
  const match = sorted.find(
    (n) =>
      rest === n.prefix
      || (n.prefix && rest.startsWith(n.prefix + "/"))
      || (n.prefix && rest.startsWith(n.prefix)),
  );
  return match ? match.label : "Orange HQ";
}
